import * as React from 'react';

import type {SelectComponentValue} from 'nutshell-core/types';

import {Radio} from 'shells/radio/radio';
import {RadioGroup} from 'shells/radio/radio-group';
// @ts-expect-error - imported from .js/.jsx file
import {CountrySelect} from 'shells/select/country-select';
import {Select} from 'shells/select/select';
import {COUNTRIES_WITH_STATES} from 'shells/select/select-constants';
// @ts-expect-error - imported from .js/.jsx file
import {StateSelect} from 'shells/select/state-select';
import {TextFieldDebounced} from 'shells/textfield/textfield-debounced';

import {LocationRadiusSearch} from '../../../base/form-components/location-radius-search';

import type {SelectedLocationFilterType, LocationFormValue} from './types';

import styles from './location-default.module.css';

const PLACEHOLDER_MAP = {
    city: 'a city',
    areaCode: 'an area code',
    postalCode: 'a postal code',
} as const;

const EXACT_MATCH_SELECT_VALUES = [
    {id: 'country', name: 'Country'},
    {id: 'state', name: 'State / province'},
    {id: 'city', name: 'City'},
    {id: 'postalCode', name: 'Postal code'},
    {id: 'areaCode', name: 'Area code'},
];

const DEFAULT_FORM_VALUES = {
    exactMatch: {
        selected: 'country',
        country: '*',
        state: '*',
    },
    range: {
        selected: 25,
        data: '',
    },
    noLocation: null,
} as const;

type Props = {
    value?: LocationFormValue;
    onChange: (formValue: {[field: string]: State}) => void;
    name: string;
};

type State = {
    selected?: SelectedLocationFilterType | null | undefined;
    data: any;
};

// This is used as the default in most places for selected the location filter, see 'FormLocationWithinUS' for when we want to default to US only
export class FormLocationDefault extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            selected: props.value && props.value.selected ? props.value.selected : null,
            data: props.value && props.value.data ? props.value.data : null,
        };
    }

    componentDidUpdate(unused: Props, prevState: State) {
        if (prevState !== this.state) {
            this.handleFormChange();
        }
    }

    render() {
        const exactMatchSelected =
            this.state.selected === 'exactMatch' && this.state.data
                ? this.state.data.selected
                : DEFAULT_FORM_VALUES.exactMatch.selected;

        const shouldShowStateSelectOption =
            (exactMatchSelected === 'state' || exactMatchSelected === 'city') &&
            COUNTRIES_WITH_STATES.includes(this.state.data.country);

        return (
            <div className={styles.container}>
                <RadioGroup
                    // @ts-expect-error
                    onChange={this.handleRadioSelect}
                    selectedValue={this.state.selected}
                    name={this.props.name}
                >
                    <Radio value='noLocation'>No location specified</Radio>
                    <Radio value='exactMatch'>Exact location</Radio>
                    {this.state.selected === 'exactMatch' ? (
                        <div className={styles['exact-match-container']}>
                            <Select
                                value={EXACT_MATCH_SELECT_VALUES.find(
                                    (val) => val.id === exactMatchSelected
                                )}
                                selectValues={EXACT_MATCH_SELECT_VALUES}
                                onChange={this.handleExactMatchSelectionChange}
                            />
                            {exactMatchSelected !== 'areaCode' ? (
                                <CountrySelect
                                    value={this.state.data.country}
                                    onChange={this.handleCountrySelectChange}
                                    statesOnly={exactMatchSelected === 'state'}
                                />
                            ) : null}
                            {shouldShowStateSelectOption ? (
                                <StateSelect
                                    value={this.state.data.state}
                                    country={this.state.data.country}
                                    onChange={this.handleStateSelectChange}
                                />
                            ) : null}
                            {exactMatchSelected !== 'country' && exactMatchSelected !== 'state' ? (
                                <TextFieldDebounced
                                    // @ts-expect-error
                                    className='ui-input--full-width'
                                    // @ts-expect-error
                                    placeholder={`Enter ${PLACEHOLDER_MAP[exactMatchSelected]}…`}
                                    value={this.state.data[exactMatchSelected]}
                                    onChange={this.handleTextSelectChange}
                                />
                            ) : null}
                        </div>
                    ) : null}
                    <Radio value='range'>Within</Radio>
                    {this.state.selected === 'range' ? (
                        <LocationRadiusSearch
                            locationString={this.getLocationStringFromState()}
                            radius={this.getRadiusFromState()}
                            // @ts-expect-error
                            popoverClass='ui-popover--location-search'
                            onLocationChange={this.handleLocationChange}
                            onRadiusChange={this.handleRadiusChange}
                        />
                    ) : null}
                </RadioGroup>
            </div>
        );
    }

    /**
     * Helper method to safely get locationString from state
     * @return {string | undefined} possible locationString
     */
    getLocationStringFromState = () => {
        if (this.state.data && this.state.data.data && this.state.data.data.locationString) {
            return this.state.data.data.locationString;
        }

        return undefined;
    };

    /**
     * Helper method to safely get radius value
     * @return {string | undefined} possible radius value
     */
    getRadiusFromState = () => {
        if (this.state.data && this.state.data.selected) {
            return this.state.data.selected;
        }

        return undefined;
    };

    handleExactMatchSelectionChange = (exactMatchObject: SelectComponentValue) => {
        this.setState((prevState) => {
            const countryVal =
                exactMatchObject.id === 'state' &&
                !COUNTRIES_WITH_STATES.includes(prevState.data.country)
                    ? 'US'
                    : prevState.data.country;

            return {
                data: {
                    ...prevState.data,
                    selected: exactMatchObject.id,
                    country: countryVal,
                },
            };
        });
    };

    handleCountrySelectChange = (countrySelectObject: SelectComponentValue) => {
        this.setState((prevState) => ({
            data: {...prevState.data, country: countrySelectObject.id},
        }));
    };

    handleStateSelectChange = (stateSelectObject: SelectComponentValue) => {
        this.setState((prevState) => ({
            data: {...prevState.data, state: stateSelectObject.id},
        }));
    };

    handleTextSelectChange = (textSelectValue: string) => {
        this.setState((prevState) => ({
            data: {...prevState.data, [prevState.data.selected]: textSelectValue},
        }));
    };

    /**
     * Called when a radio button is selected - we'll updated our state's
     * selected value, and then seed the data of that selected value with
     * our pre-canned defaults
     * @param  {SelectedLocationFilterType} selected – key of selected value
     * @return {void}
     */
    handleRadioSelect = (selected: SelectedLocationFilterType) => {
        if (selected !== this.state.selected) {
            this.setState({
                selected: selected,
                data: DEFAULT_FORM_VALUES[selected],
            });
        }
    };

    /**
     * Updates the `data.selected` of our state, which is equal to the radius
     * in this case.
     * @param  {string} newRadius updated radius value
     * @return {void}
     */
    handleRadiusChange = (newRadius: string) => {
        this.setState((prevState) => ({
            data: {...prevState.data, selected: newRadius},
        }));
    };

    /**
     * Updates the `data.data` of our state, which is equal to the location
     * object, which consists of two strings, the value and the joined
     * coordinates (by comma)
     *
     * @param  {Location} location – new Location value to update our state
     * @return {void}
     */
    handleLocationChange = (location: Location) => {
        this.setState((prevState) => ({
            data: {...prevState.data, data: location},
        }));
    };

    /**
     * Calls the parent with the new values. This has some safety around it
     * so we're not passing incomplete values when the selected form isn't
     * valid.
     *
     * @return {void}
     */
    handleFormChange = () => {
        if (this.isValidFormData()) {
            this.props.onChange({
                [this.props.name]: {
                    selected: this.state.selected,
                    data: this.state.data,
                },
            });
        }
    };

    /**
     * Checks to ensure current form data is submittable to the server.
     * It almost always is, but we want to ensure we're blocking a null
     * zipcode, area code, city name, etc.
     *
     * This is very naive - we're not validating numbers on area codes,
     * etc, but I don't believe it's a huge issue for now.
     *
     * @return {boolean} Is the form data valid or not
     */
    isValidFormData(): boolean {
        const {data, selected} = this.state;

        if (selected !== 'noLocation' && !data) {
            return false;
        }

        if (selected === 'range' && !this.getLocationStringFromState()) {
            return false;
        }

        if (selected === 'exactMatch' && data.selected !== 'country' && data.selected !== 'state') {
            return data[data.selected] && data[data.selected].length > 2;
        }

        return true;
    }
}
